import React, { useState } from 'react';
import { getSocket } from '@/lib/socket';
import { useRoomStore } from '@/stores/roomStore';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { useToastStore } from '@/components/Toast';

interface AddSongProps {
  roomId: string;
}

export const AddSong: React.FC<AddSongProps> = () => {
  const socket = getSocket();
  const { myRole } = useRoomStore();
  const addToast = useToastStore((state) => state.addToast);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const canAdd = myRole === 'owner' || myRole === 'dj';

  const extractVideoId = (input: string): string | null => {
    if (/^[a-zA-Z0-9_-]{11}$/.test(input)) {
      return input;
    }

    const patterns = [
      /music\.youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/v\/([a-zA-Z0-9_-]{11})/
    ];

    for (const pattern of patterns) {
      const match = input.match(pattern);
      if (match) return match[1];
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canAdd) {
      addToast('Only DJ and Owner can add songs', 'error');
      return;
    }

    const videoId = extractVideoId(url);

    if (!videoId) {
      addToast('Invalid YouTube URL or video ID', 'error');
      return;
    }

    const songTitle = title.trim() || `Video ${videoId}`;

    setIsLoading(true);

    socket.emit('playlist:add', {
      videoId,
      title: songTitle
    }, (response: { success: boolean; error?: string }) => {
      setIsLoading(false);

      if (response.success) {
        addToast('Song added to queue', 'success');
        setUrl('');
        setTitle('');
      } else {
        addToast(response.error || 'Failed to add song', 'error');
      }
    });
  };

  return (
    <div className="card">
      <h2 className="text-lg font-bold text-primary mb-4">Add Song</h2>

      {!canAdd ? (
        <div className="text-center py-8">
          <svg className="w-12 h-12 mx-auto text-tertiary mb-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
          <p className="text-secondary font-bold">You need DJ role to add songs</p>
          <p className="text-sm text-tertiary mt-1">Ask the room owner for permissions</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="YouTube URL or Video ID"
            placeholder="https://www.youtube.com/watch?v=..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={isLoading}
            helperText="Supports YouTube and YouTube Music links"
          />

          <Input
            label="Song Title (Optional)"
            placeholder="Leave empty to use video title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={isLoading}
            helperText="Optional - will be auto-detected if left empty"
          />

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            isLoading={isLoading}
            disabled={!url || isLoading}
          >
            Add to Queue
          </Button>

          <div className="mt-4 p-3 bg-lime-400/10 border border-lime-400/30 rounded-lg">
            <div className="flex gap-2">
              <svg className="w-5 h-5 text-lime-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-secondary">
                <p className="font-bold text-primary mb-1">Pro Tips:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Use official music videos for best quality</li>
                  <li>Check if the video is available in your region</li>
                  <li>Some videos may not work due to embedding restrictions</li>
                </ul>
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};
